import React, { useState, useEffect, useCallback } from "react";
import { AdminSidebar } from "../Sidebar/Sidebar";
import { AdminTopBar } from "../topbar/Topbar";
import { Formik, Form, Field } from "formik";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import "../Quest/Quests.css";
import "../School/Schools.css";
import BackButton from "../Goback/GoBack";
import TopImage from "../../common/TopImage";
import CustomInput from "../../../common/CustomInput";
import SuccessModal from "../../../common/SuccessModal";
import classroomImg from "../../../../assets/img/teacherDashboard/Classroom.png";
import { useCreateAgreement } from "../../../../hooks/admin/agreement/useCreateAgreement";
import { AgreementValidationSchema } from "./AgreementValidation";
import { useGetAgreementSchoolList } from "../../../../hooks/admin/agreement/useGetAgreementSchoolList";
import { useAssignQuestToSchool } from "../../../../hooks/admin/quest/useAssignQuestToSchool";
import QuestSelector from "./QuestSelector";

export const AdminCreateAgreement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [inputLayout, setInputLayout] = useState(
    window.innerWidth < 576 ? "stacked" : "inline",
  );

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Quest assignment states
  const [selectedQuestIds, setSelectedQuestIds] = useState([]);
  const [showAssignFailModal, setShowAssignFailModal] = useState(false);
  const [assignRetryPayload, setAssignRetryPayload] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const { createAgreement, loading, error } = useCreateAgreement();
  const {
    schools,
    loading: schoolLoading,
    error: schoolError,
  } = useGetAgreementSchoolList();

  const {
    assignQuestToSchool,
    loading: assignLoading,
    error: assignError,
    setError: setAssignError,
  } = useAssignQuestToSchool();

  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setInputLayout(window.innerWidth < 576 ? "stacked" : "inline");
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Quest selection callback
  const handleQuestSelectionChange = useCallback((ids) => {
    setSelectedQuestIds(ids);
  }, []);

  //  Agreement-focused initial values
  const initialValues = {
    institution_id: "",
    duration_value: "",
    duration_unit: "year",
    base_licenses: "",
    pricing_type: "bulk", // bulk | student
    price_per_unit: "",
    price_per_student: "",
    billing_mode: "offline",
    currency: "INR",
  };

  //  Submit Handler (API-safe payload)
  const handleSubmit = async (values, { resetForm }) => {
    const calculatedPrice =
      values.pricing_type === "student"
        ? Number(values.price_per_student) * Number(values.base_licenses)
        : Number(values.price_per_unit);

    const payload = {
      institution_id: Number(values.institution_id),
      billing_mode: values.billing_mode,
      duration_value: Number(values.duration_value),
      duration_unit: values.duration_unit,
      base_licenses: Number(values.base_licenses),
      price_per_unit: calculatedPrice,
      currency: values.currency,
      price_type:
        values.pricing_type === "student" ? "per_student" : "bulk_pricing",
    };

    // Step 1: Create the agreement
    await createAgreement(payload);

    // Step 2: Attempt quest assignment if any quests selected
    if (selectedQuestIds.length > 0) {
      const questPayload = {
        quest_ids: selectedQuestIds,
        remark: "Assigned via agreement creation",
      };

      try {
        await assignQuestToSchool(values.institution_id, questPayload);
        // Both succeeded
        resetForm();
        setShowSuccessModal(true);
      } catch {
        // Agreement created but quest assignment failed
        setAssignRetryPayload({
          schoolId: values.institution_id,
          questData: questPayload,
        });
        resetForm();
        setShowAssignFailModal(true);
      }
    } else {
      // No quests to assign, just show success
      resetForm();
      setShowSuccessModal(true);
    }
  };

  // Retry quest assignment
  const handleRetryAssign = async () => {
    if (!assignRetryPayload) return;
    setRetrying(true);
    setAssignError(""); // Clear previous error
    try {
      await assignQuestToSchool(
        assignRetryPayload.schoolId,
        assignRetryPayload.questData,
      );
      // Retry succeeded — close fail modal and show success
      setShowAssignFailModal(false);
      setAssignRetryPayload(null);
      setRetrying(false);
      setShowSuccessModal(true);
    } catch {
      // Still failing — keep modal open, error auto-set by hook
      setRetrying(false);
    }
  };

  // Continue without retrying
  const handleContinueWithoutAssign = () => {
    setShowAssignFailModal(false);
    setAssignRetryPayload(null);
    navigate("/school/agreements");
  };

  return (
    <div className="dashboardBody school-section">
      <AdminSidebar isSidebarCollapsed={isSidebarCollapsed} />

      <div className="container-fluid p-0">
        <div className="p-0" id="mainContent">
          <AdminTopBar toggleSidebar={toggleSidebar} />

          <div className="teacher-bodycontent mb-4">
            <section>
              <div className="container-fluid">
                <div className="row mb-3">
                  <div className="col-12">
                    <BackButton />
                  </div>
                </div>

                <TopImage imageSrc={classroomImg} text="Create Agreement" />

                <Formik
                  initialValues={initialValues}
                  validationSchema={AgreementValidationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ values }) => (
                    <Form className="mt-4">
                      <div className="school-card mb-4">
                        <h4 className="mb-4 fw-bold text-dark">
                          Create Agreement
                        </h4>
                        <hr className="mb-4 text-muted" />

                        <div className="row g-3">
                          {/* School */}
                          <div className="col-md-12">
                            <CustomInput
                              label="Select School / Organization"
                              name="institution_id"
                              type="select"
                              layout={inputLayout}
                              labelWidth={{ desktop: "200px" }}
                            >
                              <option value="" disabled>
                                Select School / Organization
                              </option>
                              {schoolLoading && (
                                <option disabled>Loading schools...</option>
                              )}

                              {!schoolLoading &&
                                schools?.map((school) => (
                                  <option key={school.id} value={school.id}>
                                    {school.name}
                                  </option>
                                ))}
                              {/* Map schools here */}
                            </CustomInput>
                          </div>

                          {/* Duration value */}
                          <div className="col-md-12">
                            <CustomInput
                              label="Duration"
                              name="duration_value"
                              type="number"
                              placeholder="Enter duration"
                              layout={inputLayout}
                              labelWidth={{ desktop: "200px" }}
                            />
                          </div>

                          {/* Duration unit */}
                          <div className="col-md-12">
                            <div
                              className={`custom-input-group ${inputLayout === "inline" ? "inline-layout" : ""
                                }`}
                              style={
                                inputLayout === "inline"
                                  ? { "--label-width-desktop": "200px" }
                                  : {}
                              }
                            >
                              <div className="label-container">
                                <label>Duration Unit *</label>
                              </div>

                              <div className="d-flex gap-4">
                                <label className="d-flex align-items-center gap-2">
                                  <Field
                                    type="radio"
                                    name="duration_unit"
                                    value="year"
                                  />
                                  Year
                                </label>

                                <label className="d-flex align-items-center gap-2">
                                  <Field
                                    type="radio"
                                    name="duration_unit"
                                    value="month"
                                  />
                                  Month
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-12">
                            <div
                              className={`custom-input-group ${inputLayout === "inline" ? "inline-layout" : ""
                                }`}
                              style={
                                inputLayout === "inline"
                                  ? { "--label-width-desktop": "200px" }
                                  : {}
                              }
                            >
                              <div className="label-container">
                                <label>Select Currency *</label>
                              </div>

                              <div className="d-flex gap-4">
                                <label className="d-flex align-items-center gap-2">
                                  <Field
                                    type="radio"
                                    name="currency"
                                    value="INR"
                                  />
                                  INR
                                </label>

                                <label className="d-flex align-items-center gap-2">
                                  <Field
                                    type="radio"
                                    name="currency"
                                    value="USD"
                                  />
                                  USD
                                </label>
                              </div>
                            </div>
                          </div>

                          {/* Licenses */}
                          <div className="col-md-12">
                            <CustomInput
                              label="No. of Licenses"
                              name="base_licenses"
                              type="number"
                              placeholder="Enter licenses"
                              layout={inputLayout}
                              labelWidth={{ desktop: "200px" }}
                            />
                          </div>

                          {/* Pricing type */}
                          <div className="col-md-12">
                            <div
                              className={`custom-input-group ${inputLayout === "inline" ? "inline-layout" : ""
                                }`}
                              style={
                                inputLayout === "inline"
                                  ? { "--label-width-desktop": "200px" }
                                  : {}
                              }
                            >
                              <div className="label-container">
                                <label>Pricing Type *</label>
                              </div>

                              <div className="d-flex gap-4">
                                <label className="d-flex align-items-center gap-2">
                                  <Field
                                    type="radio"
                                    name="pricing_type"
                                    value="bulk"
                                  />
                                  Bulk Pricing
                                </label>

                                <label className="d-flex align-items-center gap-2">
                                  <Field
                                    type="radio"
                                    name="pricing_type"
                                    value="student"
                                  />
                                  Per Student
                                </label>
                              </div>
                            </div>
                          </div>

                          {/* Bulk price */}
                          {values.pricing_type === "bulk" && (
                            <div className="col-md-12">
                              <CustomInput
                                label="Price Per Unit"
                                name="price_per_unit"
                                type="number"
                                placeholder="Enter price"
                                layout={inputLayout}
                                labelWidth={{ desktop: "200px" }}
                              />
                            </div>
                          )}

                          {/* Student price */}
                          {values.pricing_type === "student" && (
                            <div className="col-md-12">
                              <CustomInput
                                label="Price Per Student"
                                name="price_per_student"
                                type="number"
                                placeholder="Enter student price"
                                layout={inputLayout}
                                labelWidth={{ desktop: "200px" }}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Quest Selector */}
                      <QuestSelector
                        onSelectionChange={handleQuestSelectionChange}
                        disabled={loading || assignLoading}
                      />

                      {error && (
                        <span className="custom-error pb-2 d-block">
                          {error}
                        </span>
                      )}

                      <div className="row">
                        <div className="col-12">
                          <Button
                            type="submit"
                            className="custom-primary-button"
                            disabled={loading || assignLoading}
                          >
                            {loading
                              ? "Creating..."
                              : assignLoading
                                ? "Assigning Quests..."
                                : "Create Agreement"}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Full Success Modal */}
      <SuccessModal
        showModal={showSuccessModal}
        handleClose={() => setShowSuccessModal(false)}
        title="Agreement Created Successfully"
        message="The agreement has been created and quests have been assigned successfully."
        primaryButtonText="Add Agreement"
        secondaryButtonText="Agreement List"
        onPrimaryClick={() => setShowSuccessModal(false)}
        onSecondaryClick={() => navigate("/school/agreements")}
      />

      {/* Partial Success Modal (Agreement OK, Quest Assignment Failed) */}
      <SuccessModal
        showModal={showAssignFailModal}
        handleClose={() => {}} // Prevent auto-close — only close via Retry success or Continue
        type="error"
        title="Agreement Created Successfully"
        message="Agreement created successfully, but unable to assign quests to the school. You can retry now or assign them later by updating the agreement."
        primaryButtonText={retrying ? "Retrying..." : "Retry Assignment"}
        secondaryButtonText="Continue"
        onPrimaryClick={handleRetryAssign}
        onSecondaryClick={handleContinueWithoutAssign}
      >
        {assignError && (
          <p className="text-danger text-center mb-0" style={{ fontSize: 13 }}>
            <i className="bi bi-exclamation-circle me-1" />
            {assignError}
          </p>
        )}
      </SuccessModal>
    </div>
  );
};
